import NewProfileImage from '@/components/NewProfileImage';
import Table from '@/components/Table/Table';
import { TooltipWrapper } from '@/components/TooltipWrapper';
import { SatisfactionCoachRanking } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { MediumText, RegularText } from '@/lib/Text';
import { Col, Row } from '@/lib/utils';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';

type Props = {
    satisfactionCoachRankings?: SatisfactionCoachRanking[];
    selectedCoachId: string | null;
    onSelectCoach?: (coach: { code: string; value: string }) => void;
};

const columnHelper = createColumnHelper<SatisfactionCoachRanking>();

function FeedInstructorReviewsCoachRankingsByCoach({
    satisfactionCoachRankings = [],
    selectedCoachId,
    onSelectCoach,
}: Props) {
    const formatRating = useCallback((value: number) => (value === 0 ? '-' : value.toFixed(1)), []);
    const getRowState = useCallback(
        (coachId: string) => {
            if (!selectedCoachId) {
                return { isSelected: false, isDimmed: false };
            }
            const isSelected = coachId === selectedCoachId;
            return { isSelected, isDimmed: !isSelected };
        },
        [selectedCoachId],
    );

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'profile',
                header: () => (
                    <div className="flex flex-row w-full justify-start items-start h-[50px]">
                        <MediumText fontSize={18} lineHeight={25} color="#191F28" letterSpacing={-0.3}>
                            만족도 강사 순위
                        </MediumText>
                    </div>
                ),
                cell: (info) => {
                    const rowIndex = info.table.getRowModel().rows.findIndex((r: any) => r.id === info.row.id) + 1;

                    return (
                        <Row className={`flex flex-row w-full h-full items-center justify-start pl-[11px]`}>
                            <MediumText
                                fontSize={20}
                                lineHeight={30}
                                color={colors.primary}
                                className="mr-[16px] min-w-[24px]"
                            >
                                {rowIndex}
                            </MediumText>
                            <NewProfileImage
                                size={38}
                                imageUrl={info.row.original.coachProfileImageUrl}
                                className="mr-[16px]"
                                circleColor={'#F2F4F6'}
                                imageFill="#D2D8DE"
                            />
                            <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} className="line-clamp-1">
                                {info.row.original.coachName}
                            </RegularText>
                        </Row>
                    );
                },
                size: 150,
                enableSorting: false,
                meta: {
                    align: 'start',
                    isSortIconVisible: false,
                },
            }),

            columnHelper.accessor('coachTotalAverageRating', {
                id: 'total',
                header: '전체',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'total';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <div className="flex w-full h-full items-center justify-center">
                            <TooltipWrapper
                                tooltip={{
                                    opacity: 1,
                                    customContent: <Col>{/* TODO : 12명회원/100개의견  */}</Col>,
                                }}
                            >
                                <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                                    {formatRating(info.getValue())}
                                </RegularText>
                            </TooltipWrapper>
                        </div>
                    );
                },
                size: 71,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachFirstClassAverageRating', {
                id: 'first',
                header: '첫',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'first';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <div className="flex w-full h-full items-center justify-center">
                            <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                                {formatRating(info.getValue())}
                            </RegularText>
                        </div>
                    );
                },
                size: 71,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachMiddleClassAverageRating', {
                id: 'middle',
                header: '중간',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'middle';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <div className="flex w-full h-full items-center justify-center">
                            <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                                {formatRating(info.getValue())}
                            </RegularText>
                        </div>
                    );
                },
                size: 71,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachLastClassAverageRating', {
                id: 'last',
                header: '마지막',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'last';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <div className="flex w-full h-full items-center justify-center">
                            <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                                {formatRating(info.getValue())}
                            </RegularText>
                        </div>
                    );
                },
                size: 71,
                meta: {
                    align: 'center',
                },
            }),
        ],
        [formatRating, getRowState],
    );

    return (
        <div className="flex w-[474px] h-[959px] flex-col bg-[#FFFFFF] border-[0.5px] border-[#E8E8E8] rounded-[25px] overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto px-[32px] pt-[21px] pb-[40px]">
                <Table
                    data={satisfactionCoachRankings}
                    columns={columns}
                    rowHeight={62}
                    headerColor="#FFFFFF"
                    emptyText="강사 순위 데이터가 없습니다."
                    onRowClick={(row: any) => {
                        if (onSelectCoach) {
                            onSelectCoach({
                                code: row.coachId,
                                value: row.coachName,
                            });
                        }
                    }}
                    getRowClassName={(row) => {
                        if (!selectedCoachId) return '';
                        const isSelected = row.coachId === selectedCoachId;
                        const isDimmed = !isSelected;
                        return isSelected ? 'bg-[#EEF6FF]' : isDimmed ? 'opacity-40' : '';
                    }}
                />
            </div>
        </div>
    );
}

export default FeedInstructorReviewsCoachRankingsByCoach;
